import { useCallback, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';

import ActionSheet from './ActionSheet';
import { ConfirmContext } from 'src/shared/hooks/useConfirm';
import type { ConfirmFn, ConfirmOptions, ConfirmResult } from 'src/shared/hooks/useConfirm';

interface Pending {
  options: ConfirmOptions;
  resolve: (result: ConfirmResult) => void;
}

/**
 * Hosts the one confirmation dialog the app shows at a time, and hands
 * `useConfirm()` a function that opens it and waits for the answer.
 *
 * Replaces `window.confirm` and `window.prompt`: those cannot be styled, block
 * the page, and on a phone read like an error from the browser rather than a
 * question from the salon's own app.
 */
export default function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [isOpen, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  // The answer is settled once, whichever way the dialog closes.
  const settled = useRef(false);
  const reasonId = useId();

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<ConfirmResult>((resolve) => {
        settled.current = false;
        setReason('');
        setPending({ options, resolve });
        setOpen(true);
      }),
    []
  );

  const answer = (confirmed: boolean) => {
    if (pending && !settled.current) {
      settled.current = true;
      pending.resolve({ confirmed, reason: confirmed ? reason.trim() : '' });
    }
    setOpen(false);
  };

  const options = pending?.options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <ActionSheet
          isOpen={isOpen}
          toggle={() => answer(false)}
          title={options.title}
          subtitle={options.message}
          onClosed={() => setPending(null)}
          footer={
            <>
              <Button color="secondary" outline onClick={() => answer(false)}>
                {options.cancelLabel ?? 'Volver'}
              </Button>
              <Button
                color={options.tone === 'danger' ? 'danger' : 'primary'}
                onClick={() => answer(true)}
              >
                {options.confirmLabel ?? 'Confirmar'}
              </Button>
            </>
          }
        >
          {options.reason && (
            <FormGroup className="px-2 mb-0">
              <Label for={reasonId}>{options.reason.label}</Label>
              <Input
                id={reasonId}
                type="textarea"
                rows={2}
                maxLength={255}
                placeholder={options.reason.placeholder}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </FormGroup>
          )}
        </ActionSheet>
      )}
    </ConfirmContext.Provider>
  );
}
